import { useEffect, useState } from "react";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Shield, LogOut, Clock, Monitor, MapPin, FileLock2, ExternalLink, BookOpen } from "lucide-react";
import { fmtDateTime } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PrivacyCard() {
  const { t } = useTranslation();
  const [acc, setAcc] = useState(null);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get("/privacy-notice/my-acceptance").then((r) => setAcc(r.data)).catch(() => {});
    api.get("/privacy-notice/my-history").then((r) => setHistory(r.data || [])).catch(() => {});
  }, []);
  if (!acc || !acc.required || !acc.current_notice) return null;
  const n = acc.current_notice;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="privacy-profile-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] flex items-center justify-center flex-shrink-0">
          <FileLock2 className="w-6 h-6 text-[#1A1A1A]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("privacyProfile.title")}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {t("privacyProfile.versionLine", { n: n.version_number, date: n.effective_date })}
          </p>
          <p className="text-sm mt-1" data-testid="privacy-profile-status">
            {acc.already_accepted
              ? <span className="text-emerald-700">{t("privacyProfile.acceptedOn", { date: fmtDateTime(acc.accepted_at) })}</span>
              : <span className="text-amber-600">{t("privacyProfile.pending")}</span>}
          </p>
          <a href={`${BACKEND_URL}${n.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="privacy-profile-view-pdf"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#1A1A1A] hover:underline">
            <ExternalLink className="w-4 h-4" /> {t("privacyProfile.viewPdf")}
          </a>
          {history.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="text-xs font-bold text-slate-500 mb-1.5">{t("privacyProfile.historyTitle")}</div>
              {history.map((h) => (
                <div key={h.id} className="text-xs text-slate-500 flex items-center justify-between py-0.5">
                  <span>v{h.privacy_notice_version_number}</span>
                  <span>{fmtDateTime(h.accepted_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CodeOfConductCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/supplier-code-of-conduct/my-status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  if (!status || !status.required || !status.current_document) return null;
  const doc = status.current_document;
  const pdfUrl = `${BACKEND_URL}${doc.pdf_url}`;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="code-profile-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-6 h-6 text-[#1A1A1A]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-[#1A1A1A]">Codigo de Conducta para Proveedores</h3>
          <p className="text-sm text-slate-500 mt-1">
            Version vigente: {doc.version_number} (desde {doc.effective_date})
          </p>
          <p className="text-sm mt-1" data-testid="code-profile-status">
            {status.accepted
              ? <span className="text-emerald-700">Aceptaste la version {status.accepted_version} el {fmtDateTime(status.accepted_at)}</span>
              : <span className="text-amber-600">Pendiente de aceptacion</span>}
          </p>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="code-profile-view-pdf"
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#1A1A1A] hover:underline">
            <ExternalLink className="w-4 h-4" /> Ver documento
          </a>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ title, confirmText, onConfirm, onClose, danger }) {
  const { t } = useTranslation();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onConfirm(pw); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div data-testid="security-password-modal" className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{t("security.confirmPasswordPrompt")}</p>
        <input
          data-testid="security-modal-password"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="mt-3 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          placeholder={t("changePassword.oldPassword")}
          autoFocus
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold hover:bg-slate-50">
            {t("common.cancel")}
          </button>
          <button
            data-testid="security-modal-confirm"
            onClick={submit}
            disabled={busy || !pw}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-[#1A1A1A] hover:bg-black"}`}
          >
            {busy ? t("common.saving") : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfileSecurity() {
  const { t } = useTranslation();
  const { logout, refreshUser, user } = useAuth();
  const [me, setMe] = useState(null);
  const [modal, setModal] = useState(null); // 'enable' | 'disable'
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const isSupplier = user?.role === "supplier";

  const load = async () => {
    const r = await api.get("/auth/me");
    setMe(r.data);
  };
  useEffect(() => { load(); }, []);

  const toggle2fa = async (password) => {
    try {
      const endpoint = modal === "enable" ? "/auth/2fa/enable" : "/auth/2fa/disable";
      await api.post(endpoint, { password });
      toast.success(modal === "enable" ? t("security.twofaEnabled") : t("security.twofaDisabled"));
      setModal(null);
      await load();
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const doLogoutAll = async () => {
    try {
      await api.post("/auth/logout-all-sessions");
      toast.success(t("security.logoutAllDone"));
      setTimeout(() => logout(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const twofaOn = me?.two_fa_enabled;
  const twofaMandatory = !isSupplier && !!me?.two_fa_mandatory;

  return (
    <>
      <Topbar title={t("security.pageTitle")} subtitle={t("security.pageSubtitle")} />
      <div className="p-6 max-w-2xl space-y-5" data-testid="profile-security-page">
        <PrivacyCard />
        <CodeOfConductCard />
        {/* 2FA card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          {isSupplier ? (
            <div data-testid="twofa-supplier-note" className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("security.twofaTitle")}</h3>
                <p className="text-sm text-slate-500 mt-1">{t("security.twofaSupplierNote")}</p>
              </div>
            </div>
          ) : twofaMandatory ? (
            <div data-testid="twofa-mandatory-note" className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("security.twofaTitle")}</h3>
                  <span data-testid="twofa-status-on" className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    {t("security.active")}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{t("security.twofaMandatoryNote")}</p>
              </div>
            </div>
          ) : (
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${twofaOn ? "bg-emerald-50" : "bg-slate-100"}`}>
              {twofaOn ? <ShieldCheck className="w-6 h-6 text-emerald-600" /> : <Shield className="w-6 h-6 text-slate-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("security.twofaTitle")}</h3>
                {twofaOn && (
                  <span data-testid="twofa-status-on" className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    {t("security.active")}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">{t("security.twofaDescription")}</p>
            </div>
            {me && (twofaOn ? (
              <button
                data-testid="twofa-disable-btn"
                onClick={() => setModal("disable")}
                className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 whitespace-nowrap"
              >
                {t("security.disable")}
              </button>
            ) : (
              <button
                data-testid="twofa-enable-btn"
                onClick={() => setModal("enable")}
                className="btn-primary-chizy px-4 py-2 rounded-xl text-sm whitespace-nowrap"
              >
                {t("security.enable")}
              </button>
            ))}
          </div>
          )}
        </div>

        {/* Last login card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-lg text-[#1A1A1A] mb-3">{t("security.lastLoginTitle")}</h3>
          {me?.last_login_at ? (
            <div data-testid="last-login-info" className="space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> {fmtDateTime(me.last_login_at)}</div>
              <div className="flex items-center gap-2"><Monitor className="w-4 h-4 text-slate-400" /> {me.last_login_user_agent || "—"}</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {me.last_login_ip || "—"}</div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">{t("security.noLastLogin")}</p>
          )}
        </div>

        {/* Logout all sessions */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <LogOut className="w-6 h-6 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("security.logoutAllTitle")}</h3>
              <p className="text-sm text-slate-500 mt-1">{t("security.logoutAllDescription")}</p>
              <p className="text-xs text-slate-400 mt-1.5">{t("security.logoutAllOldSessionsNote")}</p>
            </div>
            <button
              data-testid="logout-all-btn"
              onClick={() => setConfirmLogoutAll(true)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-bold hover:bg-slate-50 whitespace-nowrap"
            >
              {t("security.logoutAllAction")}
            </button>
          </div>
        </div>
      </div>

      {modal && (
        <PasswordModal
          title={modal === "enable" ? t("security.enableTitle") : t("security.disableTitle")}
          confirmText={modal === "enable" ? t("security.enable") : t("security.disable")}
          danger={modal === "disable"}
          onConfirm={toggle2fa}
          onClose={() => setModal(null)}
        />
      )}

      {confirmLogoutAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmLogoutAll(false)}>
          <div data-testid="logout-all-modal" className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("security.logoutAllTitle")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("security.logoutAllConfirm")}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setConfirmLogoutAll(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold hover:bg-slate-50">
                {t("common.cancel")}
              </button>
              <button
                data-testid="logout-all-confirm"
                onClick={doLogoutAll}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700"
              >
                {t("security.logoutAllAction")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
